import {useNavigate} from "react-router-dom";
import {App as AntdApp, Button, Tag} from "antd";
import {FontAwesomeIcon} from "~/components";
import className from "classnames/bind";
import Markdown from "./Markdown";
import style from "../style/AiTool.module.scss";

const cn = className.bind(style);

/** Nút copy nhanh 1 đoạn text. */
function CopyBtn({text, label = 'Copy'}) {
	const {message} = AntdApp.useApp();
	const copy = () => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(text);
			message.success('Đã copy');
		}
	};
	return (
		<Button size="small" type="text" onClick={copy}
			icon={<FontAwesomeIcon icon="fa-light fa-copy" />}>
			{label}
		</Button>
	);
}

/** Dòng meta có đếm ký tự + cảnh báo vượt ngưỡng. */
function MetaLine({title, value, limit}) {
	const len = (value || '').length;
	const over = limit && len > limit;
	return (
		<div className={cn('metaLine')}>
			<div className={cn('metaHead')}>
				<span className={cn('metaLabel')}>{title}</span>
				<span className={cn('metaCount', {over})}>{len}{limit ? `/${limit}` : ''} ký tự</span>
				<CopyBtn text={value || ''} />
			</div>
			<div className={cn('metaValue')}>{value}</div>
		</div>
	);
}

function MetaView({output}) {
	const variants = Array.isArray(output.variants) ? output.variants : [];
	return (
		<div>
			<div className={cn('block')}>
				<MetaLine title="Meta Title" value={output.title} limit={60} />
				<MetaLine title="Meta Description" value={output.description} limit={160} />
			</div>
			{variants.length > 0 && (
				<div className={cn('variants')}>
					<div className={cn('subTitle')}>Biến thể khác</div>
					{variants.map((v, i) => (
						<div key={i} className={cn('block')}>
							<MetaLine title={`Title ${i + 1}`} value={v.title} limit={60} />
							<MetaLine title={`Description ${i + 1}`} value={v.description} limit={160} />
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function FaqView({output}) {
	const faqs = Array.isArray(output.faqs) ? output.faqs : [];
	const allText = faqs.map((f) => `${f.question}\n${f.answer}`).join('\n\n');
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag>{faqs.length} câu hỏi</Tag>
				<CopyBtn text={allText} label="Copy tất cả" />
			</div>
			{faqs.map((f, i) => (
				<div key={i} className={cn('faqItem')}>
					<div className={cn('faqQ')}>
						<FontAwesomeIcon icon="fa-light fa-circle-question" /> {f.question}
					</div>
					<div className={cn('faqA')}>{f.answer}</div>
				</div>
			))}
		</div>
	);
}

function OutlineView({output}) {
	const outline = Array.isArray(output.outline) ? output.outline : [];
	return (
		<div>
			{output.title && (
				<div className={cn('block')}>
					<MetaLine title="Tiêu đề bài viết" value={output.title} />
				</div>
			)}
			<ul className={cn('outline')}>
				{outline.map((h, i) => (
					<li key={i}>
						<span className={cn('h2')}>{h.heading}</span>
						{Array.isArray(h.children) && h.children.length > 0 && (
							<ul>
								{h.children.map((c, j) => <li key={j} className={cn('h3')}>{c}</li>)}
							</ul>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}

function KeywordsView({output}) {
	const kws = Array.isArray(output.keywords) ? output.keywords : [];
	const allText = kws.map((k) => k.keyword).join('\n');
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag>{kws.length} từ khoá</Tag>
				<CopyBtn text={allText} label="Copy tất cả" />
			</div>
			{kws.map((k, i) => (
				<div key={i} className={cn('kwItem')}>
					<span className={cn('kwName')}>{k.keyword}</span>
					{k.intent && <Tag color="blue">{k.intent}</Tag>}
					{k.type && <Tag>{k.type}</Tag>}
				</div>
			))}
		</div>
	);
}

function TitlesView({output}) {
	const titles = Array.isArray(output.titles) ? output.titles : [];
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag>{titles.length} tiêu đề</Tag>
				<CopyBtn text={titles.join('\n')} label="Copy tất cả" />
			</div>
			{titles.map((t, i) => (
				<div key={i} className={cn('titleItem')}>
					<span className={cn('titleText')}>{t}</span>
					<span className={cn('titleLen', {over: (t || '').length > 60})}>{(t || '').length}</span>
					<CopyBtn text={t} />
				</div>
			))}
		</div>
	);
}

function InternalLinksView({output}) {
	const items = Array.isArray(output.suggestions) ? output.suggestions : [];
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag>{items.length} gợi ý liên kết</Tag>
				<CopyBtn text={items.map((s) => `${s.anchor} → ${s.target}`).join('\n')} label="Copy tất cả" />
			</div>
			{items.map((s, i) => (
				<div key={i} className={cn('faqItem')}>
					<div className={cn('faqQ')}>
						<FontAwesomeIcon icon="fa-light fa-link" /> {s.anchor}
						<Tag color="blue" style={{marginLeft: 8}}>{s.target}</Tag>
					</div>
					<div className={cn('faqA')}>
						{s.context && <p><strong>Vị trí chèn:</strong> {s.context}</p>}
						{s.reason && <p><strong>Lý do:</strong> {s.reason}</p>}
					</div>
				</div>
			))}
		</div>
	);
}

const ANCHOR_TYPE_COLOR = {'chính xác': 'red', 'một phần': 'orange', 'thương hiệu': 'purple', 'tự nhiên': 'green'};

function AnchorsView({output}) {
	const anchors = Array.isArray(output.anchors) ? output.anchors : [];
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag>{anchors.length} anchor</Tag>
				<CopyBtn text={anchors.map((a) => a.text).join('\n')} label="Copy tất cả" />
			</div>
			{anchors.map((a, i) => (
				<div key={i} className={cn('kwItem')}>
					<span className={cn('kwName')}>{a.text}</span>
					{a.type && <Tag color={ANCHOR_TYPE_COLOR[a.type] || 'default'}>{a.type}</Tag>}
					<CopyBtn text={a.text} />
				</div>
			))}
		</div>
	);
}

/** Kết quả "Bài viết bắt trend": các góc bài + dàn ý ngắn + từ khoá phụ. */
function TrendAnglesView({output}) {
	const angles = Array.isArray(output.angles) ? output.angles : [];
	const allText = angles
		.map((a) => `${a.title}\n${a.angle || ''}\n${(a.outline || []).join('\n')}`)
		.join('\n\n');
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag>{angles.length} góc bài viết</Tag>
				<CopyBtn text={allText} label="Copy tất cả" />
			</div>
			{angles.map((a, i) => (
				<div key={i} className={cn('faqItem')}>
					<div className={cn('faqQ')}>
						<FontAwesomeIcon icon="fa-light fa-fire" /> {a.title}
					</div>
					<div className={cn('faqA')}>
						{a.angle && <p><strong>Góc tiếp cận:</strong> {a.angle}</p>}
						{Array.isArray(a.outline) && a.outline.length > 0 && (
							<ul>
								{a.outline.map((h, j) => <li key={j}>{h}</li>)}
							</ul>
						)}
						{Array.isArray(a.keywords) && a.keywords.length > 0 && (
							<p>{a.keywords.map((k, j) => <Tag key={j}>{k}</Tag>)}</p>
						)}
					</div>
				</div>
			))}
		</div>
	);
}

function SchemaView({output}) {
	const navigate = useNavigate();
	const obj = output && output.jsonld ? output.jsonld : output;
	const json = JSON.stringify(obj, null, 2);
	const snippet = `<script type="application/ld+json">\n${json}\n</script>`;
	return (
		<div>
			<div className={cn('resultToolbar')}>
				<Tag color="purple">JSON-LD</Tag>
				<CopyBtn text={json} label="Copy JSON" />
				<CopyBtn text={snippet} label="Copy kèm <script>" />
				<Button size="small" type="text" onClick={() => navigate('/schema/validator', {state: {json}})}
					icon={<FontAwesomeIcon icon="fa-light fa-shield-check" />}>
					Kiểm tra bằng Validator
				</Button>
			</div>
			<pre className={cn('rawJson')}>{json}</pre>
		</div>
	);
}

/**
 * Render kết quả AI theo tác vụ + format.
 * - json + output có cấu trúc → view chuyên biệt (meta/faq/outline).
 * - còn lại → text thô. Nếu JSON parse hỏng (output null) cũng fallback text thô.
 */
function AiResult({result}) {
	const {task, output, text, format} = result;
	const meta = [result.provider, result.model].filter(Boolean).join(' · ');

	let body;
	if (format === 'json' && output) {
		if (task === 'meta') body = <MetaView output={output} />;
		else if (task === 'faq') body = <FaqView output={output} />;
		else if (task === 'outline') body = <OutlineView output={output} />;
		else if (task === 'keywords') body = <KeywordsView output={output} />;
		else if (task === 'titles') body = <TitlesView output={output} />;
		else if (task === 'schema') body = <SchemaView output={output} />;
		else if (task === 'internal_links') body = <InternalLinksView output={output} />;
		else if (task === 'anchor_text') body = <AnchorsView output={output} />;
		else if (task === 'trend_content') body = <TrendAnglesView output={output} />;
		else body = <pre className={cn('rawJson')}>{JSON.stringify(output, null, 2)}</pre>;
	} else {
		// Bài viết đầy đủ trả markdown (heading ##/###) → render có định dạng.
		const isMarkdown = task === 'article';
		body = (
			<div>
				<div className={cn('resultToolbar')}>
					<CopyBtn text={text || ''} label="Copy" />
				</div>
				{isMarkdown
					? <Markdown content={text} className={cn('rawText')} />
					: <div className={cn('rawText')}>{text}</div>}
			</div>
		);
	}

	return (
		<div className={cn('result')}>
			<div className={cn('resultHead')}>
				<Tag color="blue">Kết quả AI</Tag>
				{meta && <span className={cn('resultMeta')}>{meta}</span>}
			</div>
			{body}
		</div>
	);
}

export default AiResult;
